import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormDataType } from 'contexts/FormProvider';
import { AUSTRALIA } from 'utils/constants';
import { useAddressChange, createAddressChangeHandler } from './useAddressChange';
import { ADDRESS_FIELD_MAPS } from 'components/AddressSearch/addressHelpers';

interface UseContactOptionsAddressHandlerParams {
  form: UseFormReturn<FormDataType>;
  // TODO: QAS_CLEANUP - Remove this parameter after toggle decommission
  DcomCustQasMigrationToggle: boolean;
  isComplaint: boolean;
}

export const useContactOptionsAddressHandler = ({
  form,
  DcomCustQasMigrationToggle,
  isComplaint,
}: UseContactOptionsAddressHandlerParams) => {
  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
    clearErrors: clearError,
  } = form;

  const addressType = watch('addressType') || '';

  // TODO: QAS_CLEANUP - Remove all code below until "Address management integration" comment after toggle decommission - START
  const useQasLegacyLogic = !DcomCustQasMigrationToggle;

  const isManualPhysAddress = watch('isManualPhysAddress');
  const isPhysicalCountryAU = watch('phyCountry') === AUSTRALIA;
  const isManualPosAddress = watch('isManualPosAddress');
  const isPostalCountryAU = watch('posCountry') === AUSTRALIA;

  const hasValidManualFields =
    !errors.phyStreetAddress && !errors.phySuburb && !errors.phyCountry && !errors.phyState && !errors.phyPostcode;

  // Handle manual physical address validation
  useEffect(() => {
    if (!useQasLegacyLogic) {
      return;
    }

    if (isManualPhysAddress && !isComplaint) {
      clearError('physicalAddress');

      if (hasValidManualFields) {
        setValue('isManualPhysAddressValid', hasValidManualFields);
      } else {
        setValue('isManualPhysAddressValid', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualPhysAddress, hasValidManualFields, isComplaint, useQasLegacyLogic]);

  // Handle physical state registration
  useEffect(() => {
    if (!useQasLegacyLogic) {
      return;
    }

    if (isManualPhysAddress && isPhysicalCountryAU) {
      register('phyState');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualPhysAddress, isPhysicalCountryAU, useQasLegacyLogic]);

  // Handle postal state registration
  useEffect(() => {
    if (!useQasLegacyLogic) {
      return;
    }

    if (isManualPosAddress && isPostalCountryAU) {
      register('posState');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualPosAddress, isPostalCountryAU, useQasLegacyLogic]);
  // TODO: QAS_CLEANUP - Remove all code above until "Address management integration" comment after toggle decommission - END

  // Address management integration
  const { processAddressChange, getDefaults } = useAddressChange();
  const physicalDefaults = getDefaults({ type: 'physical', formValues: watch() });
  const postalDefaults = getDefaults({ type: 'postal', formValues: watch() });

  const handleAddressChange = createAddressChangeHandler(setValue, processAddressChange);

  const showPostalAddress = isComplaint && addressType === 'no';

  return {
    showPostalAddress,
    handleAddressChange,
    physicalDefaults,
    postalDefaults,
    // TODO: QAS_CLEANUP - Remove these properties after toggle decommission
    isManualPhysAddress: useQasLegacyLogic && isManualPhysAddress,
    isManualPosAddress: useQasLegacyLogic && isManualPosAddress,
  };
};
